import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { SmartphoneIcon, Volume2Icon, ArrowRightIcon, AlertTriangleIcon, ShieldIcon, EyeIcon, CheckCircle2Icon, XCircleIcon, HelpCircleIcon, PrinterIcon, MailIcon, LinkIcon, QrCodeIcon } from 'lucide-react';
// --- Slide Animation Variants ---
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
export function ScamPhishingModulePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  // Part 3 State
  const [practiceAnswer, setPracticeAnswer] = useState<'correct' | 'incorrect' | null>(null);
  // Part 4 State
  const [reflectionAnswer, setReflectionAnswer] = useState<string | null>(null);
  // Part 5 State
  const [taskChecked, setTaskChecked] = useState(false);
  const [showProof, setShowProof] = useState(false);
  const [safeWord, setSafeWord] = useState('');
  const [completed, setCompleted] = useState(false);
  const nextStep = () => {
    if (step < 5) {
      setDirection(1);
      setStep(step + 1);
    }
  };
  const prevStep = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    }
  };
  const getStepName = () => {
    switch (step) {
      case 1:
        return 'Context';
      case 2:
        return 'What to Know';
      case 3:
        return 'Try It';
      case 4:
        return 'Your Household';
      case 5:
        return 'Your Action';
      default:
        return '';
    }
  };
  return <div className={`min-h-screen flex flex-col font-sans overflow-hidden ${step === 1 ? 'bg-[#1a0647]' : 'bg-[#f8f8f8]'}`} data-id="element-4795">
      {/* --- Persistent Header --- */}
      <header className="px-6 py-4 z-20 relative" data-id="element-4796">
        <div className="flex items-center justify-between mb-3" data-id="element-4797">
          <p className="text-[#01ced1] font-bold text-sm tracking-wider uppercase" data-id="element-4798">
            Step {step} of 5 — {getStepName()}
          </p>
          {step > 1 && <button onClick={prevStep} className={`text-sm font-bold ${step === 1 ? 'text-white' : 'text-gray-500'}`} data-id="element-4799">
              Back
            </button>}
        </div>
        <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-4800">
          <motion.div className="h-full bg-[#01ced1]" initial={{
          width: '0%'
        }} animate={{
          width: `${step / 5 * 100}%`
        }} transition={{
          duration: 0.3
        }} data-id="element-4801" />
        </div>
      </header>

      {/* --- Main Content Area --- */}
      <main className="flex-1 relative" data-id="element-4802">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-4803">
          {/* --- PART 1: HOOK --- */}
          {step === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col" data-id="element-4804">
              <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full" data-id="element-4805">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-8 relative" data-id="element-4806">
                  <SmartphoneIcon size={32} className="text-white" data-id="element-4807" />
                  <div className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full border-2 border-[#1a0647]" data-id="element-4808"></div>
                </div>

                <h1 className="text-3xl font-bold text-white mb-6 leading-tight" data-id="element-4809">
                  It happened to a family just like yours.
                </h1>

                <p className="text-lg text-[#d0bdf5] mb-6 leading-relaxed" data-id="element-4810">
                  Dan got a call from someone claiming to be his bank. They knew
                  his name and last four digits. He transferred $3,200 before
                  realizing it was a scam.
                </p>

                <p className="text-xl font-bold text-[#01ced1] mb-10" data-id="element-4811">
                  Sound familiar?
                </p>

                <button className="flex items-center gap-3 bg-white/10 text-white px-5 py-3 rounded-xl w-fit hover:bg-white/20 transition-colors" data-id="element-4812">
                  <Volume2Icon size={20} data-id="element-4813" />
                  <span className="font-medium" data-id="element-4814">Listen to this story</span>
                </button>
              </div>

              <div className="mt-auto pt-8 pb-safe max-w-md mx-auto w-full flex justify-end" data-id="element-4815">
                <button onClick={nextStep} className="w-14 h-14 rounded-full bg-[#01ced1] text-[#1a0647] flex items-center justify-center hover:bg-[#00b8bc] transition-colors shadow-lg" data-id="element-4816">
                  <ArrowRightIcon size={24} data-id="element-4817" />
                </button>
              </div>
            </motion.div>}

          {/* --- PART 2: CONCEPT --- */}
          {step === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col overflow-y-auto" data-id="element-4818">
              <div className="max-w-md mx-auto w-full pb-24" data-id="element-4819">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-8 leading-tight" data-id="element-4820">
                  Scammers use one trick above all others: urgency.
                </h1>

                <div className="space-y-6 mb-8" data-id="element-4821">
                  {/* Concept 1 */}
                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200" data-id="element-4822">
                    <div className="flex items-center gap-3 mb-4" data-id="element-4823">
                      <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0" data-id="element-4824">
                        <AlertTriangleIcon size={20} className="text-red-500" data-id="element-4825" />
                      </div>
                      <h3 className="font-bold text-[#111111] text-lg" data-id="element-4826">
                        They pressure you to act NOW
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4827">
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-4828">
                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1 block" data-id="element-4829">
                          Scam
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-4830">
                          "Your account will be suspended in 24 hours if you
                          don't verify."
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100" data-id="element-4831">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1 block" data-id="element-4832">
                          Real
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-4833">
                          "Your statement is ready to view online."
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Concept 2 */}
                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200" data-id="element-4834">
                    <div className="flex items-center gap-3 mb-4" data-id="element-4835">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-4836">
                        <ShieldIcon size={20} className="text-blue-500" data-id="element-4837" />
                      </div>
                      <h3 className="font-bold text-[#111111] text-lg" data-id="element-4838">
                        They claim to be someone official
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4839">
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-4840">
                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1 block" data-id="element-4841">
                          Scam
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-4842">
                          "This is the IRS. You owe back taxes."
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100" data-id="element-4843">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1 block" data-id="element-4844">
                          Real
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-4845">
                          The IRS will only contact you via physical mail first.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Concept 3 */}
                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200" data-id="element-4846">
                    <div className="flex items-center gap-3 mb-4" data-id="element-4847">
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0" data-id="element-4848">
                        <EyeIcon size={20} className="text-purple-500" data-id="element-4849" />
                      </div>
                      <h3 className="font-bold text-[#111111] text-lg" data-id="element-4850">
                        They know just enough to seem real
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4851">
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-4852">
                        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1 block" data-id="element-4853">
                          Scam
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-4854">
                          "Hi John, regarding your recent Amazon order ending in
                          4921..."
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100" data-id="element-4855">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1 block" data-id="element-4856">
                          Real
                        </span>
                        <p className="text-xs text-gray-800" data-id="element-4857">
                          Data breaches make this info public. Don't trust it.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <button className="flex items-center gap-3 bg-white border border-gray-200 text-[#111111] px-5 py-3 rounded-xl w-full justify-center hover:bg-gray-50 transition-colors shadow-sm mb-8" data-id="element-4858">
                  <Volume2Icon size={20} className="text-[#8528e2]" data-id="element-4859" />
                  <span className="font-medium" data-id="element-4860">Listen to a 30-second tip</span>
                </button>

                <button onClick={nextStep} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md" data-id="element-4861">
                  Continue
                </button>
              </div>
            </motion.div>}

          {/* --- PART 3: PRACTICE --- */}
          {step === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col overflow-y-auto" data-id="element-4862">
              <div className="max-w-md mx-auto w-full pb-24" data-id="element-4863">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-8" data-id="element-4864">
                  Is this a scam?
                </h1>

                {/* Simulated Phone Screen */}
                <div className="bg-white rounded-[2rem] border-8 border-gray-200 shadow-lg p-4 mb-8 relative overflow-hidden" data-id="element-4865">
                  <div className="w-20 h-5 bg-gray-200 rounded-b-xl absolute top-0 left-1/2 -translate-x-1/2" data-id="element-4866"></div>
                  <div className="pt-6 pb-2 border-b border-gray-100 flex items-center justify-center mb-4" data-id="element-4867">
                    <span className="font-bold text-sm text-gray-800" data-id="element-4868">
                      Messages
                    </span>
                  </div>
                  <div className="bg-gray-100 rounded-2xl p-4 text-sm text-gray-800 relative" data-id="element-4869">
                    <p data-id="element-4870">
                      ALERT: Your Chase account has been locked. Click here to
                      restore access:{' '}
                      <span className="text-blue-500 underline" data-id="element-4871">
                        chase-secure-alert.net/verify
                      </span>
                    </p>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gray-100 rounded-full" data-id="element-4872"></div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-4873">
                  <button onClick={() => setPracticeAnswer('correct')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${practiceAnswer === 'correct' ? 'bg-[#8528e2] text-white ring-4 ring-purple-200' : 'bg-[#8528e2] text-white hover:bg-[#7322c4]'}`} data-id="element-4874">
                    Yes, this is a scam
                  </button>
                  <button onClick={() => setPracticeAnswer('incorrect')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all border-2 ${practiceAnswer === 'incorrect' ? 'border-red-500 text-red-600 bg-red-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`} data-id="element-4875">
                    Looks legitimate
                  </button>
                </div>

                {/* Feedback Area */}
                <AnimatePresence data-id="element-4876">
                  {practiceAnswer === 'correct' && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5" data-id="element-4877">
                      <div className="flex items-start gap-3" data-id="element-4878">
                        <CheckCircle2Icon className="text-green-600 shrink-0 mt-0.5" size={24} data-id="element-4879" />
                        <div data-id="element-4880">
                          <h3 className="font-bold text-green-800 mb-1" data-id="element-4881">
                            Great catch!
                          </h3>
                          <p className="text-sm text-green-700 mb-4" data-id="element-4882">
                            The URL "chase-secure-alert.net" is fake, and banks
                            will never text you a link to unlock your account.
                          </p>
                          <button onClick={nextStep} className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-green-700 transition-colors" data-id="element-4883">
                            Continue
                          </button>
                        </div>
                      </div>
                    </motion.div>}

                  {practiceAnswer === 'incorrect' && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5" data-id="element-4884">
                      <div className="flex items-start gap-3" data-id="element-4885">
                        <HelpCircleIcon className="text-amber-600 shrink-0 mt-0.5" size={24} data-id="element-4886" />
                        <div data-id="element-4887">
                          <h3 className="font-bold text-amber-800 mb-1" data-id="element-4888">
                            Good instinct to pause.
                          </h3>
                          <p className="text-sm text-amber-700" data-id="element-4889">
                            Here's what to look for: The link says
                            "chase-secure-alert.net" instead of just
                            "chase.com". Banks will never text you a link to log
                            in. Try again!
                          </p>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>}

          {/* --- PART 4: REFLECTION --- */}
          {step === 4 && <motion.div key="step4" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 px-6 py-8 flex flex-col overflow-y-auto" data-id="element-4890">
              <div className="max-w-md mx-auto w-full pb-24" data-id="element-4891">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-2" data-id="element-4892">
                  Think about your household.
                </h1>
                <p className="text-lg text-gray-600 mb-8" data-id="element-4893">
                  Who in your family is most likely to receive a message like
                  this?
                </p>

                <div className="space-y-3 mb-8" data-id="element-4894">
                  {['Me', 'My parents', 'My kids', 'Everyone'].map(option => <button key={option} onClick={() => setReflectionAnswer(option)} className={`w-full p-5 rounded-xl border-2 text-left font-bold text-lg transition-all ${reflectionAnswer === option ? 'border-[#8528e2] bg-[#f0ebf8] text-[#8528e2]' : 'border-gray-200 bg-white text-[#111111] hover:border-[#8528e2] hover:bg-gray-50'}`} data-id="element-4895">
                      {option}
                    </button>)}
                </div>

                <AnimatePresence data-id="element-4896">
                  {reflectionAnswer && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-8" data-id="element-4897">
                      <p className="text-blue-800 font-medium" data-id="element-4898">
                        {reflectionAnswer === 'My parents' ? 'Parents are the most targeted group for financial scams. Module 8 covers this in detail.' : reflectionAnswer === 'My kids' ? "Kids are often targeted through social media and gaming platforms. We'll cover that soon." : 'Scammers cast a wide net. Everyone is a target, which is why family-wide awareness is key.'}
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {reflectionAnswer && <motion.button initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} onClick={nextStep} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md" data-id="element-4899">
                    Continue
                  </motion.button>}
              </div>
            </motion.div>}

          {/* --- PART 5: TAKEAWAY + VERIFICATION --- */}
          {step === 5 && <motion.div key="step5" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          duration: 0.3
        }} className="absolute inset-0 flex flex-col overflow-y-auto bg-white" data-id="element-4900">
              <div className="h-2 w-full bg-[#01ced1]" data-id="element-4901"></div>
              <div className="px-6 py-8 max-w-md mx-auto w-full pb-32" data-id="element-4902">
                <h1 className="text-2xl font-bold text-[#1a0647] mb-6" data-id="element-4903">
                  Your one action right now.
                </h1>

                <div className="bg-[#f8f8f8] p-6 rounded-2xl border border-gray-200 mb-8" data-id="element-4904">
                  <h2 className="text-xl font-bold text-[#111111] mb-4" data-id="element-4905">
                    Set up your family safe word.
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-4906">
                    A word only your family knows to verify real emergencies. If
                    someone calls claiming to be a family member in trouble, ask
                    for the safe word.
                  </p>

                  {!showProof && <div className="space-y-6" data-id="element-4907">
                      <label className="flex items-start gap-4 cursor-pointer group" data-id="element-4908">
                        <div className="relative flex items-center justify-center mt-1" data-id="element-4909">
                          <input type="checkbox" className="sr-only" checked={taskChecked} onChange={e => setTaskChecked(e.target.checked)} data-id="element-4910" />
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${taskChecked ? 'bg-[#8528e2] border-[#8528e2]' : 'border-gray-300 group-hover:border-[#8528e2]'}`} data-id="element-4911">
                            {taskChecked && <CheckCircle2Icon size={16} className="text-white" data-id="element-4912" />}
                          </div>
                        </div>
                        <span className="text-lg font-bold text-[#111111]" data-id="element-4913">
                          I have agreed on a safe word with my family.
                        </span>
                      </label>

                      <AnimatePresence data-id="element-4914">
                        {taskChecked && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} className="space-y-6 overflow-hidden" data-id="element-4915">
                            <p className="text-sm text-gray-500 italic" data-id="element-4916">
                              Before you confirm: this step matters for your
                              family's protection. Please only mark it done if
                              you actually completed it. It's completely okay to
                              come back to this later.
                            </p>
                            <button onClick={() => setShowProof(true)} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md" data-id="element-4917">
                              Mark as Complete
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </div>}

                  {showProof && !completed && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="space-y-4" data-id="element-4918">
                      <label className="block text-sm font-bold text-[#111111]" data-id="element-4919">
                        What is your family's safe word?
                      </label>
                      <p className="text-xs text-gray-500 mb-2" data-id="element-4920">
                        We won't store it — this is just to confirm you set one
                        up.
                      </p>
                      <input type="text" value={safeWord} onChange={e => setSafeWord(e.target.value)} className="w-full p-4 rounded-xl border border-gray-300 focus:border-[#8528e2] focus:ring-2 focus:ring-purple-200 outline-none text-lg" placeholder="Enter safe word" data-id="element-4921" />
                      <button disabled={!safeWord.trim()} onClick={() => setCompleted(true)} className="w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4922">
                        Verify
                      </button>
                    </motion.div>}

                  {completed && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="text-center py-4" data-id="element-4923">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4" data-id="element-4924">
                        <CheckCircle2Icon size={32} className="text-green-600" data-id="element-4925" />
                      </div>
                      <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-4926">
                        Verified Complete!
                      </h3>
                      <div className="inline-block bg-[#01ced1] text-[#1a0647] font-bold px-4 py-2 rounded-full text-sm mb-6" data-id="element-4927">
                        +50 pts
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6 text-left" data-id="element-4928">
                        <p className="text-sm font-bold text-[#111111] mb-3" data-id="element-4929">
                          Share this task:
                        </p>
                        <div className="flex justify-between gap-2" data-id="element-4930">
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-4931">
                            <PrinterIcon size={20} data-id="element-4932" />
                            <span className="text-[10px] font-medium" data-id="element-4933">
                              Print
                            </span>
                          </button>
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-4934">
                            <MailIcon size={20} data-id="element-4935" />
                            <span className="text-[10px] font-medium" data-id="element-4936">
                              Email
                            </span>
                          </button>
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-4937">
                            <LinkIcon size={20} data-id="element-4938" />
                            <span className="text-[10px] font-medium" data-id="element-4939">
                              Copy
                            </span>
                          </button>
                          <button className="flex-1 flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-4940">
                            <QrCodeIcon size={20} data-id="element-4941" />
                            <span className="text-[10px] font-medium" data-id="element-4942">QR</span>
                          </button>
                        </div>
                      </div>

                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-100 mb-8 text-left" data-id="element-4943">
                        <p className="text-sm text-purple-900 font-medium mb-3" data-id="element-4944">
                          Dan completed this step too. Want to let him know?
                        </p>
                        <button className="w-full py-2 bg-white border border-purple-200 text-purple-700 rounded-lg font-bold text-sm hover:bg-purple-100 transition-colors" data-id="element-4945">
                          Send High Five ✋
                        </button>
                      </div>

                      <Link to="/dashboard" className="block w-full bg-[#8528e2] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#7322c4] transition-colors shadow-md no-underline" data-id="element-4946">
                        Continue to Next Action
                      </Link>
                    </motion.div>}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </main>

      {/* --- Persistent 'I'm Stuck' Button --- */}
      <div className="fixed bottom-6 left-6 z-30" data-id="element-4947">
        <button className="bg-white text-gray-600 border border-gray-200 shadow-lg rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-gray-50 transition-colors" data-id="element-4948">
          <HelpCircleIcon size={18} data-id="element-4949" />
          I'm Stuck
        </button>
      </div>
    </div>;
}